import { planDownscale } from "../domain/resize";
import { getAsset, putAsset } from "./assets";

// 背景除去（Web Worker で実行）のクライアント。
// 推論はワーカー側に隔離されているため、実行中も UI の描画・操作は固まらない。
// 画像はワーカーとの間で転送されるだけで、端末外へは送信されない

export class ModelLoadError extends Error {}
export class RemovalError extends Error {}

export interface ModelProgress {
  loaded: number;
  total: number;
}

// 推論入力の事前縮小の上限長辺。モデルの入力解像度（1024×1024）に合わせる。
// これ以上の解像度を渡しても精度は上がらず、転送・前処理が重くなるだけ
const INFERENCE_MAX_EDGE = 1024;

type WorkerMessage =
  | { type: "ready" }
  | { type: "progress"; file: string; loaded: number; total: number }
  | { type: "removed"; id: number; bitmap: ImageBitmap }
  | { type: "load_error"; message: string }
  | { type: "run_error"; id: number; message: string };

let worker: Worker | null = null;
let readyPromise: Promise<void> | null = null;
let readyResolve: (() => void) | null = null;
let readyReject: ((e: Error) => void) | null = null;
let requestSeq = 0;
const pending = new Map<
  number,
  { resolve: (assetId: string) => void; reject: (e: Error) => void }
>();
let progressListener: ((p: ModelProgress) => void) | null = null;

export function onModelProgress(listener: (p: ModelProgress) => void): void {
  progressListener = listener;
}

function handleMessage(event: MessageEvent): void {
  const data = event.data as WorkerMessage;
  switch (data.type) {
    case "ready":
      readyResolve?.();
      break;
    case "progress":
      // 進捗はモデル本体（.onnx）のダウンロードを代表値として通知する
      if (data.file.endsWith(".onnx") && data.total > 0) {
        progressListener?.({ loaded: data.loaded, total: data.total });
      }
      break;
    case "removed": {
      const entry = pending.get(data.id);
      pending.delete(data.id);
      entry?.resolve(putAsset(data.bitmap));
      break;
    }
    case "load_error": {
      const error = new ModelLoadError(data.message);
      readyReject?.(error);
      for (const [id, entry] of pending) {
        pending.delete(id);
        entry.reject(error);
      }
      // 次回の preload で新しいワーカーから再試行できるようにする
      worker?.terminate();
      worker = null;
      readyPromise = null;
      break;
    }
    case "run_error": {
      const entry = pending.get(data.id);
      pending.delete(data.id);
      entry?.reject(new RemovalError(data.message));
      break;
    }
  }
}

function ensureWorker(): Worker {
  if (worker) return worker;
  worker = new Worker(new URL("../workers/removal.worker.ts", import.meta.url), {
    type: "module",
  });
  worker.addEventListener("message", handleMessage);
  return worker;
}

// 素材取り込みステップの表示中に呼び、切り抜きステップの待ち時間を短縮する
export function preloadModel(): Promise<void> {
  if (readyPromise) return readyPromise;
  readyPromise = new Promise<void>((resolve, reject) => {
    readyResolve = resolve;
    readyReject = reject;
  });
  ensureWorker().postMessage({ type: "preload" });
  return readyPromise;
}

async function toInferenceBitmap(assetId: string): Promise<ImageBitmap> {
  const asset = getAsset(assetId);
  if (!asset) throw new RemovalError("素材が見つかりません。");
  const plan = planDownscale(asset.width, asset.height, INFERENCE_MAX_EDGE);
  return createImageBitmap(asset, {
    resizeWidth: plan.width,
    resizeHeight: plan.height,
    resizeQuality: "high",
  });
}

// 背景除去を実行し、結果画像のアセットIDを返す
export async function removeBackground(assetId: string): Promise<string> {
  await preloadModel();
  const bitmap = await toInferenceBitmap(assetId);
  const id = ++requestSeq;
  const promise = new Promise<string>((resolve, reject) => {
    pending.set(id, { resolve, reject });
  });
  ensureWorker().postMessage({ type: "remove", id, bitmap }, [bitmap]);
  return promise;
}
